import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';

import { ILookupItem, IRequisitionItemForm } from './forms.models';

type Props = {
  open: boolean;
  title: string;
  value: IRequisitionItemForm;
  resourceTypes: ILookupItem[];
  uoms: ILookupItem[];
  priorities: ILookupItem[];
  onClose: () => void;
  onSave: (value: IRequisitionItemForm) => void;
};

type State = {
  form: IRequisitionItemForm;
};

export default class RequisitionItemDialog extends React.Component<any, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      form: { ...props.value },
    };
  }

  componentDidUpdate(prevProps: Props): void {
    if (
      prevProps.open !== this.props.open ||
      prevProps.value !== this.props.value
    ) {
      this.setState({
        form: { ...this.props.value },
      });
    }
  }

  private handleTextChange =
    (field: keyof IRequisitionItemForm) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value;

      this.setState(
        (prevState) => ({
          form: {
            ...prevState.form,
            [field]: value,
          },
        }),
        this.recomputeTotalsIfNeeded
      );
    };

  private handleNumberChange =
    (field: keyof IRequisitionItemForm) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const rawValue = event.target.value;
      const value = rawValue === '' ? '' : Number(rawValue);

      this.setState(
        (prevState) => ({
          form: {
            ...prevState.form,
            [field]: value,
          },
        }),
        this.recomputeTotalsIfNeeded
      );
    };

  private handleSelectChange =
    (field: keyof IRequisitionItemForm) =>
    (event: SelectChangeEvent<string>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        form: {
          ...prevState.form,
          [field]: value,
        },
      }));
    };

  private recomputeTotalsIfNeeded = (): void => {
    this.setState((prevState) => {
      const quantity =
        prevState.form.quantity === '' ? null : Number(prevState.form.quantity);
      const unitestimate =
        prevState.form.unitestimate === ''
          ? null
          : Number(prevState.form.unitestimate);

      if (
        quantity !== null &&
        !Number.isNaN(quantity) &&
        unitestimate !== null &&
        !Number.isNaN(unitestimate)
      ) {
        return {
          form: {
            ...prevState.form,
            totalestimate: Number((quantity * unitestimate).toFixed(2)),
          },
        };
      }

      return null;
    });
  };

  private handleSave = (): void => {
    this.props.onSave({ ...this.state.form });
  };

  render(): React.ReactNode {
    const { open, title, onClose, resourceTypes, uoms, priorities } = this.props;
    const { form } = this.state;

    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{title}</DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                label="Line No"
                type="number"
                value={form.lineno}
                onChange={this.handleNumberChange('lineno')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                label="Item Title"
                value={form.recname}
                onChange={this.handleTextChange('recname')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                label="Short Code"
                value={form.shortcode}
                onChange={this.handleTextChange('shortcode')}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                multiline
                minRows={2}
                value={form.description}
                onChange={this.handleTextChange('description')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel id="item-type-label">Resource Type</InputLabel>
                <Select
                  labelId="item-type-label"
                  value={form.typeix}
                  label="Resource Type"
                  onChange={this.handleSelectChange('typeix')}
                >
                  {resourceTypes.map((item:ILookupItem) => (
                    <MenuItem key={item.recix} value={item.recix}>
                      {item.recname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={form.quantity}
                onChange={this.handleNumberChange('quantity')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="uom-label">UOM</InputLabel>
                <Select
                  labelId="uom-label"
                  value={form.uomix}
                  label="UOM"
                  onChange={this.handleSelectChange('uomix')}
                >
                  {uoms.map((item:ILookupItem) => (
                    <MenuItem key={item.recix} value={item.recix}>
                      {item.recname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                label="Unit Estimate"
                type="number"
                value={form.unitestimate}
                onChange={this.handleNumberChange('unitestimate')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                label="Total Estimate"
                type="number"
                value={form.totalestimate}
                onChange={this.handleNumberChange('totalestimate')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Needed From"
                type="date"
                value={form.neededfrom}
                onChange={this.handleTextChange('neededfrom')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Needed To"
                type="date"
                value={form.neededto}
                onChange={this.handleTextChange('neededto')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel id="priority-label">Priority</InputLabel>
                <Select
                  labelId="priority-label"
                  value={form.priorityix}
                  label="Priority"
                  onChange={this.handleSelectChange('priorityix')}
                >
                  {priorities.map((item:ILookupItem) => (
                    <MenuItem key={item.recix} value={item.recix}>
                      {item.recname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="KPI Note"
                value={form.kpinote}
                onChange={this.handleTextChange('kpinote')}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Justification"
                multiline
                minRows={2}
                value={form.justification}
                onChange={this.handleTextChange('justification')}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Expected Outcome"
                multiline
                minRows={2}
                value={form.expectedoutcome}
                onChange={this.handleTextChange('expectedoutcome')}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Technical Specification"
                multiline
                minRows={2}
                value={form.technicalspec}
                onChange={this.handleTextChange('technicalspec')}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={this.handleSave}>
            Save Item
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}